import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { getSpeechLabAccessToken } from './api'
import { getTranscriptText } from '../Utils/transcriptTextFormatter'
import { makeTimestampsClickable, handleTimestampLinkClick } from '../Utils/timestampLinks'
import DOMPurify from 'dompurify'
import { FaCommentDots, FaInfoCircle } from 'react-icons/fa'
import './QATab.css'

const MAX_CONSECUTIVE_POLL_ERRORS = 3
const TIMEOUT_DURATION = 180000 // 3 minutes
const POLL_INTERVAL_DURATION = 3000 // 3 seconds
const LOAD_MORE_SCROLL_THRESHOLD = 2 // 2 pixels from the bottom of the scroll container

const QATab = ({ transcriptData, transcriptXMLPath, fileName, mediaId, onTimestampClick }) => {
    const [qaQuestion, setQaQuestion] = useState('')
    const [qaHistory, setQaHistory] = useState([])
    const [qaLoading, setQaLoading] = useState(false)
    const [qaTaskId, setQaTaskId] = useState(null)
    const [pendingQuestion, setPendingQuestion] = useState('')
    const [isPolling, setIsPolling] = useState(false)
    const [pollTimeoutReached, setPollTimeoutReached] = useState(false)
    const [hasMoreHistory, setHasMoreHistory] = useState(false)
    const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false)

    const pollIntervalRef = useRef(null)
    const pollTimeoutRef = useRef(null)
    const isRequestInFlightRef = useRef(false)
    const consecutiveErrorCountRef = useRef(0)
    const isLoadingMoreHistoryRef = useRef(false)

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current)
            pollTimeoutRef.current = null
        }
        isRequestInFlightRef.current = false
        consecutiveErrorCountRef.current = 0
    }

    // reset state when the tab unmounts
    useEffect(() => {
        return () => {
            stopPolling()
            setQaLoading(false)
            setPollTimeoutReached(false)
            setQaTaskId(null)
        }
    }, [])

    // load past Q&A and resume a pending question (if any)
    useEffect(() => {
        const loadExistingData = async () => {
            if (!mediaId) return
            try {
                const accessToken = await getSpeechLabAccessToken()
                const [historyResponse, taskStatusResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_STUDIO_URL}/api/ai/qa-history?mediaId=${mediaId}`, {
                        headers: { Authorization: `Bearer ${accessToken}` }
                    }),
                    axios.get(`${process.env.REACT_APP_STUDIO_URL}/api/ai/qa-task-status?mediaId=${mediaId}`, {
                        headers: { Authorization: `Bearer ${accessToken}` }
                    })
                ])
                if (historyResponse.data.history) {
                    setQaHistory(historyResponse.data.history)
                    setHasMoreHistory(Boolean(historyResponse.data.hasMore))
                }

                const { taskId, status, question } = taskStatusResponse.data
                if (taskId && status === 'processing') {
                    setQaTaskId(taskId)
                    setPendingQuestion(question)
                    setPollTimeoutReached(true)
                    toast.info('A question is still being answered')
                }
            } catch (error) {
                console.error('Error loading existing Q&A data:', error)
            }
        }
        loadExistingData()
    }, [mediaId])

    const loadMoreHistory = async () => {
        if (isLoadingMoreHistoryRef.current || !hasMoreHistory || qaHistory.length === 0) return
        const oldest = qaHistory[qaHistory.length - 1]
        if (!oldest?.createdAt) return

        isLoadingMoreHistoryRef.current = true
        setIsLoadingMoreHistory(true)
        try {
            const accessToken = await getSpeechLabAccessToken()
            const response = await axios.get(
                `${process.env.REACT_APP_STUDIO_URL}/api/ai/qa-history?mediaId=${mediaId}&before=${encodeURIComponent(oldest.createdAt)}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            )
            await new Promise(resolve => setTimeout(resolve, 600))
            setQaHistory(prev => [...prev, ...(response.data.history || [])])
            setHasMoreHistory(Boolean(response.data.hasMore))
        } catch (error) {
            console.error('Error loading more Q&A history:', error)
        } finally {
            isLoadingMoreHistoryRef.current = false
            setIsLoadingMoreHistory(false)
        }
    }

    const handleHistoryScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target
        if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_SCROLL_THRESHOLD) {
            loadMoreHistory()
        }
    }

    const startPolling = (taskId, accessToken) => {
        pollIntervalRef.current = setInterval(async () => {
            if (isRequestInFlightRef.current) return
            isRequestInFlightRef.current = true
            try {
                const pollResponse = await axios.get(
                    `${process.env.REACT_APP_STUDIO_URL}/api/ai/qa-poll/${taskId}`,
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                )
                consecutiveErrorCountRef.current = 0
                if (pollResponse.data.status === 'done') {
                    stopPolling()
                    setIsPolling(false)
                    setQaHistory(prev => [{ question: pollResponse.data.question, answer: pollResponse.data.answer }, ...prev])
                    setQaTaskId(null)
                    setPendingQuestion('')
                    toast.success('Answer generated successfully!')
                    setQaLoading(false)
                } else if (pollResponse.data.status === 'error') {
                    stopPolling()
                    setIsPolling(false)
                    toast.error('Failed to generate answer')
                    setQaTaskId(null)
                    setPendingQuestion('')
                    setQaLoading(false)
                }
            } catch (error) {
                console.error('Error polling:', error)
                consecutiveErrorCountRef.current += 1
                if (consecutiveErrorCountRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
                    stopPolling()
                    setIsPolling(false)
                    setQaLoading(false)
                    setPollTimeoutReached(true)
                    toast.error('Connection lost while checking answer status')
                }
            } finally {
                isRequestInFlightRef.current = false
            }
        }, POLL_INTERVAL_DURATION)
    }

    const startPollingTimeout = () => {
        pollTimeoutRef.current = setTimeout(() => {
            stopPolling()
            setIsPolling(false)
            setPollTimeoutReached(true)
            setQaLoading(false)
            toast.error('Answer generation in progress', { icon: '🕑', duration: 3000 })
        }, TIMEOUT_DURATION)
    }

    const handleAskQuestion = async () => {
        if (!qaQuestion.trim()) {
            toast.error('Please enter a question')
            return
        }
        if (qaLoading || isPolling || pollTimeoutReached) {
            return
        }

        setQaLoading(true)
        setPollTimeoutReached(false)
        stopPolling()

        const question = qaQuestion.trim()
        try {
            const transcriptText = getTranscriptText(transcriptData, transcriptXMLPath)
            const accessToken = await getSpeechLabAccessToken()
            const response = await axios.post(
                `${process.env.REACT_APP_STUDIO_URL}/api/ai/qa`,
                {
                    transcript: transcriptText,
                    question,
                    mediaId,
                    fileName: fileName || ''
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            )

            setQaTaskId(response.data.taskId)
            setPendingQuestion(question)
            setQaQuestion('')
            setIsPolling(true)
            startPolling(response.data.taskId, accessToken)
            startPollingTimeout()
        } catch (error) {
            console.error('Error asking question:', error)
            toast.error('Failed to get answer. Please try again.')
            setQaLoading(false)
        }
    }

    const handleManualPoll = async () => {
        if (!qaTaskId) return

        setQaLoading(true)
        const currentTaskId = qaTaskId
        const pollPromise = (async () => {
            const accessToken = await getSpeechLabAccessToken()
            const response = await axios.get(
                `${process.env.REACT_APP_STUDIO_URL}/api/ai/qa-poll/${currentTaskId}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            )
            await new Promise(resolve => setTimeout(resolve, 1000))
            return response
        })()

        toast.promise(
            pollPromise,
            {
                loading: 'Checking answer status...',
                success: (response) => {
                    if (response.data.status === 'done') {
                        setQaHistory(prev => [{ question: response.data.question, answer: response.data.answer }, ...prev])
                        setQaTaskId(null)
                        setPendingQuestion('')
                        setPollTimeoutReached(false)
                        return 'Answer generated successfully'
                    } else if (response.data.status === 'error') {
                        setQaTaskId(null)
                        setPendingQuestion('')
                        setPollTimeoutReached(false)
                        throw new Error('Failed to generate answer')
                    } else {
                        throw new Error('Answer generation in progress')
                    }
                },
                error: (err) => err.message || 'Failed to poll answer status'
            },
            {
                loading: { duration: 3000 },
                error: { duration: 3000, icon: '🕑' }
            }
        ).finally(() => {
            setQaLoading(false)
        })
    }

    const qaIsEmpty = qaHistory.length === 0 && !qaLoading && !isPolling && !pollTimeoutReached
    return (
        <div className="qa-content">
            <div className="qa-input-group">
                <input
                    type="text"
                    className="qa-input"
                    placeholder="Ask a question about the transcript..."
                    value={qaQuestion}
                    onChange={e => setQaQuestion(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleAskQuestion()}
                    disabled={qaLoading || isPolling || pollTimeoutReached}
                />
                <button
                    className="btn btn-primary"
                    onClick={handleAskQuestion}
                    disabled={qaLoading || isPolling || pollTimeoutReached}
                >
                    {qaLoading || isPolling ? 'Asking...' : 'Ask'}
                </button>
            </div>
            {pollTimeoutReached && (
                <div className="ai-status-container">
                    <div className="ai-status-message">
                        <FaInfoCircle />
                        {pendingQuestion ? `Still answering: "${pendingQuestion}"` : 'Answer generation in progress'}
                    </div>
                    <button
                        className="btn btn-secondary"
                        onClick={handleManualPoll}
                        disabled={qaLoading}
                    >
                        {qaLoading ? 'Checking...' : 'Check Status'}
                    </button>
                </div>
            )}
            {(qaLoading || isPolling) && !pollTimeoutReached && qaHistory.length === 0 && (
                <div className="qa-generating-state">
                    <span className="spinner"></span>
                    <p>Answering "{pendingQuestion}"...</p>
                    <p className="qa-generating-subtitle">
                        This may take a few moments.
                    </p>
                </div>
            )}
            {(qaLoading || isPolling) && !pollTimeoutReached && qaHistory.length > 0 && (
                <div className="ai-result" style={{ opacity: 0.6 }}>
                    <div className="ai-status-message">
                        <span className="spinner-small"></span>
                        Answering "{pendingQuestion}"...
                    </div>
                </div>
            )}
            {qaHistory.length > 0 && (
                <div className="qa-history" onScroll={handleHistoryScroll}>
                    <h3>Previous Q&A</h3>
                    {qaHistory.map((qa, index) => (
                        <div key={index} className="qa-item">
                            <p className="qa-question">
                                <span className="qa-label">Q:</span>
                                <span className="qa-text">{qa.question.trim()}</span>
                            </p>
                            <div className="qa-answer">
                                <span className="qa-label">A:</span>
                                <div
                                    className="qa-text"
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(makeTimestampsClickable(qa.answer))
                                    }}
                                    onClick={(e) => handleTimestampLinkClick(e, onTimestampClick)}
                                />
                            </div>
                        </div>
                    ))}
                    {isLoadingMoreHistory && (
                        <div className="qa-history-loading-more">
                            <span className="spinner-small"></span>
                            Loading older questions...
                        </div>
                    )}
                </div>
            )}
            {qaIsEmpty && (
                <div className="qa-empty-state">
                    <FaCommentDots className="qa-empty-icon" />
                    <p className="qa-empty-title">No questions yet</p>
                    <p className="qa-empty-subtitle">
                        Ask something about the transcript to see an answer here.
                    </p>
                </div>
            )}
        </div>
    )
}

export default QATab
